import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import AppContext from "../../../AppContext";
import { Calendar, DatePicker } from "@progress/kendo-react-dateinputs";
import DriverDailyTable from "./DriverDailyTable";
import axios from "axios";
import { BASE_URL } from "../../../constant";
import { useTranslation } from "react-i18next";
import GraphAnalytics from "../cost/GraphAnalytics";

const SafetyAnalytics = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { assets, darkMode } = useContext(AppContext);
  const [filterState, setFilterState] = useState({
    asset: "all",
    timeMode: "weekToDate",
    startDate: null,
    endDate: null,
    date: new Date(),
  });
  const [assetData, setAssetData] = useState([{ id: "all", name: "ALL" }]);
  const [tableData, setTableData] = useState();
  const [calculatedData, setCalculatedData] = useState({
    doorOpenMovementTimeHrs: [],
  });

  const handleApplyFilter = () => {
    const { asset, date } = filterState;
    const assetName =
      asset !== "all" ? assetData.find((x) => x.id === asset).name : asset;
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const apiUrl = `${BASE_URL}/api/AnalyticsData?name=${encodeURIComponent(
      assetName
    )}&year=${year}&month=${month}`;

    // Make the API call using Axios
    axios
      .get(apiUrl)
      .then((response) => {
        // Handle the API response here
        formatData(response.data);
        // You can update your state or perform other actions based on the response
      })
      .catch((error) => {
        // Handle errors
        console.error("Error fetching data:", error);
        // You can set an error state or display an error message to the user
      });
  };

  const getWeekdayName = (dayIndex) => {
    const weekdays = [
      t("masters.labels.sunday"),
      t("masters.labels.monday"),
      t("masters.labels.tuesday"),
      t("masters.labels.wednesday"),
      t("masters.labels.thursday"),
      t("masters.labels.friday"),
      t("masters.labels.saturday"),
    ];
    return weekdays[dayIndex];
  };

  const formatData = (dataArray) => {
    const tableData = dataArray.map((item) => ({
      vehicle: item.vehicle,
      date: new Date(item.local_date).toLocaleDateString(),
      weekDayName: getWeekdayName(new Date(item.local_date).getDay()),
      highSpeed: item.highSpeedAbove100Count,
      highRpm: item.engineRPMAbove3500Hrs,
      idleTime: item.idleTimeOnIgnitionHrs,
      doorOpened: item.doorOpenMovementTimeHrs,
    }));

    setTableData(tableData);

    const doorOpenMovementTime = dataArray.map((item) => ({
      x: new Date(item.local_date),
      y: item.doorOpenMovementTimeHrs * 60,
    }));

    setCalculatedData({
      doorOpenMovementTime,
    });
  };

  useEffect(() => {
    if (assets)
      setAssetData([
        { id: "all", name: "ALL" },
        ...assets.map((x) => ({
          id: x.id,
          name: x.name,
        })),
      ]);
  }, [assets]);

  useEffect(() => {
    handleApplyFilter();
  }, []);

  const GetCalendar = (props) => {
    return <Calendar {...props} bottomView="year" topView="decade" />;
  };

  return (
    <div className="flex flex-col p-5 h-[90vh] overflow-y-hidden dark:bg-neutral-900 bg-lightModeBg">
      <div className="font-medium dark:text-white text-labels flex items-center justify-between">
        <div className="flex text-xl items-center gap-2">
          <button type="button" onClick={() => navigate("/analytics")}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2.0"
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 12h-15m0 0l6.75 6.75M4.5 12l6.75-6.75"
              />
            </svg>
          </button>
          {t("masters.headerTexts.safetyAnalyt")}
        </div>
        <div className="flex gap-2">
          <div id="compo" className={`${darkMode ? "darkCombo" : "lightCombo"}`}>
            <ComboBox
              id="default-search"
              textField="name"
              data={assetData}
              dataItemKey="id"
              name="asset"
              style={{
                fontSize: "12px",
                fontWeight: "500",
                borderColor: "transparent",
              }}
              clearButton={false}
              className=" block border-gray-400 dark:text-white text-inputFieldsTxts rounded-lg mb-4 h-8"
              placeholder="Select Asset"
              onChange={(event) =>
                setFilterState((prevState) => ({
                  ...prevState,
                  asset: event.target.value.id,
                }))
              }
              value={assetData.find((x) => x.id === filterState.asset) || null}
              required
            />
          </div>
          <div className={`inline-flex relative rounded group h-8 z-30 w-fit dark:border-black border ${darkMode ? "darkCombo" : "lightCombo"}`}>
            <DatePicker
              format="MMMM-yyy"
              style={{
                fontSize: "12px",
                fontWeight: "500",
                borderColor: "transparent",
              }}
              className=" block border-gray-400 dark:text-white text-inputFieldsTxts rounded-lg mb-4 h-8"
              value={filterState.date}
              onChange={(e) =>
                setFilterState((pre) => ({ ...pre, date: e.value }))
              }
              calendar={(props) => GetCalendar(props)}
            />
          </div>
          <button
            type="button"
            className="dark:bg-indigo-600 bg-applyBtn md:px-2 text-xs px-1 font-medium md:h-7 h-5 my-auto rounded"
            onClick={handleApplyFilter}
          >
            {t("button.apply")}
          </button>
        </div>
      </div>
      <div className="flex flex-col max-h-[80vh] pb-2 pt-4 gap-10">
        <div className="h-1/2 w-full space-y-2">
          <label className="text-base dark:text-white text-labels font-medium">
            {t("masters.labels.movementObserveOndoorOpen")}
          </label>
          <div className="h-full">
            <GraphAnalytics
              id={4}
              data={[calculatedData.doorOpenMovementTime]}
              graphType="Line"
            />
          </div>
        </div>
        <div className="h-1/2 w-full space-y-2">
          <label className="text-base dark:text-white text-labels font-medium">
            {t("masters.labels.driverDailyScore")}
          </label>
          <div className="h-full">
            <DriverDailyTable data={tableData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SafetyAnalytics;
